import { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isEmpty, isNil, isNull, keys, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { DropdownOption, ProcessTemplateWithAiTemplate } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectUserActivePartnerTimeZone } from 'store/slices/userData';
import { batchChangeTemplateValueForAllLanguages } from 'store/slices/template';
// Import Components
import TemplateCategoryInput from './TemplateCategoryInput';
import DynamicVariables from './DynamicVariables';
import HTMLEditors from './HTMLEditors';
// Import UI Components
import { DatePickerInput, Dropdown, ExtendedInput, SkeletonLoader, TimePicker } from '@geneui/components';
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import {
    DateToISOStringDateFormat,
    DynamicPopUpDeviceTypesLabels,
    ExpirationTypes,
    l,
    TemplateEditorModes,
    TemplateInputTypes,
    TimeZonePack,
} from 'constants/common';
import { DefaultLanguage } from 'constants/template';
// Import Hooks
import useBind from 'hooks/UseBind';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useFieldValidity from 'hooks/UseFieldValidity';
import useInputInsertion from 'hooks/UseInputInsertion';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import { useQueryParams, useValidator } from 'hooks';
// Import Helpers
import { normalizeDatePickerValue } from 'components/CustomerJourneyStepper/steps/InformationStep';
// Import Services
import { UtilsHttpService } from 'services/http';
import { buildDynamicPopUpAiBody as buildDynamicPopUpRequestBody, parseDynamicPopUpAiBody } from 'services/template';

const TIMEZONE_OPTIONS = sortBy(
    keys(TimeZonePack).map((key) => ({
        label: TimeZonePack[key],
        value: +key,
    })),
    ['value'],
);

const DeviceTypeOptions = [
    { value: DynamicPopUpDeviceTypesLabels.Both, label: l.Both },
    { value: DynamicPopUpDeviceTypesLabels.Desktop, label: l.Desktop },
    { value: DynamicPopUpDeviceTypesLabels.Mobile, label: l.Mobile },
];

// temporarily hardcoded value, it shows sub content name for the same template type, it enables possibility to have different contents for the same template
const templateFieldWithConfig = 'Body';

const DynamicPopUpContent = (props) => {
    const {
        templateData,
        templateLangKey,
        handleChange,
        handleUIConfigChange,
        getAssets,
        addAsset,
        dynamicVariables,
        eventId,
    } = props;

    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const dispatch = useAppDispatch();
    const validator = useValidator();

    const templateQueryParams = useQueryParams();
    const { notification } = templateQueryParams.decode() || {};
    const isFromNotification = !isEmpty(notification);

    const { partnerDateFormat } = usePartnerDateFormats();
    const partnerTimeZone = useAppSelector(selectUserActivePartnerTimeZone) ?? 0;

    const urlTemplateData = templateData?.[TemplateInputTypes.Url];
    const bodyValue = templateData?.[TemplateInputTypes.Body];
    const uiConfig = templateData?.UIConfig;
    const expirationType = templateData?.[TemplateInputTypes.ExpirationType];
    const expirationPeriod = templateData?.[TemplateInputTypes.ExpirationPeriod];
    const expirationDate = templateData?.[TemplateInputTypes.ExpirationDate];
    const expirationTime = templateData?.[TemplateInputTypes.ExpirationTime];
    const timezone = templateData?.[TemplateInputTypes.Timezone] ?? partnerTimeZone;
    const deviceType = templateData?.[TemplateInputTypes.DeviceType];

    const isDefaultLanguage = templateLangKey === DefaultLanguage;

    const [url, setUrl] = useState(urlTemplateData ?? '');
    const [urlTypesData, setUrlTypesData] = useState<{ value: string; label: string }[]>([]);
    const [isLoadingComparisons, setIsLoadingComparisons] = useState(true);

    const { getPopUpComparisons } = useCancelableRequests({
        getPopUpComparisons: UtilsHttpService.getPopUpComparisons,
    });

    const translatedDeviceTypes = useMemo(
        () => DeviceTypeOptions.map((type) => ({ ...type, label: t(type.label) })),
        [t],
    );
    const radioGroupOptions = useMemo(
        () => TemplateEditorModes.DynamicPopUp.map((option) => ({ ...option, label: t(option.label) })),
        [t],
    );

    const expirationTypeOptions = useMemo<DropdownOption<string>[]>(
        () => [
            {
                label: t(l.FromSentDate),
                value: ExpirationTypes.ExpirationPeriod,
            },
            ...(!isFromNotification
                ? [
                      {
                          label: t(l.ToGivenDate),
                          value: ExpirationTypes.ExpirationDate,
                      },
                  ]
                : []),
        ],
        [isFromNotification, t],
    );

    const bodyValidationState = useFieldValidity(TemplateInputTypes.Body, l.Body, templateLangKey);
    const expirationTypeValidationState = useFieldValidity(TemplateInputTypes.ExpirationType, l.ExpirationType, '');
    const expirationPeriodValidationState = useFieldValidity(TemplateInputTypes.ExpirationPeriod, l.Days, '');
    const expirationDateValidationState = useFieldValidity(TemplateInputTypes.ExpirationDate, l.Date, '');
    const expirationTimeValidationState = useFieldValidity(TemplateInputTypes.ExpirationTime, l.Time, '');
    const timezoneValidationState = useFieldValidity(TemplateInputTypes.Timezone, l.Timezone, '');

    const { insertionHandler, updateInsertionHandler, insertionOptions, setInsertionOptions } = useInputInsertion();

    const handleBodyChange = useBind(handleChange, TemplateInputTypes.Body);
    const handleCategoryChange = useBind(handleChange, TemplateInputTypes.Category);
    const handleBodyUIConfigChange = useBind(handleUIConfigChange, templateFieldWithConfig);
    const updateBodyInsertionHandler = useBind(updateInsertionHandler, TemplateInputTypes.Body);
    const handleUrlChange = useBind(handleChange, TemplateInputTypes.Url);
    const handleUrlTypeChange = useBind(handleChange, TemplateInputTypes.UrlType);
    const handleDeviceTypeChange = useBind(handleChange, TemplateInputTypes.DeviceType);

    const categoryValue = templateData?.[TemplateInputTypes.Category];

    const urlTypeChangeHandler = (e) => handleUrlTypeChange(e.value, 0);
    const urlChangeHandler = (e) => {
        setUrl(e.target.value);
        handleUrlChange(e.target.value);
    };

    const handleValidateAndChange = (type, period, date, time, timezone) => {
        const expirationDateTimeValue = { type, date, time, timezone };
        const expirationPeriodValue = { type, period };

        dispatch(
            batchChangeTemplateValueForAllLanguages([
                {
                    propertyName: TemplateInputTypes.ExpirationType,
                    value: type,
                },
                {
                    propertyName: TemplateInputTypes.ExpirationPeriod,
                    value: period,
                },
                {
                    propertyName: TemplateInputTypes.ExpirationDate,
                    value: date,
                },
                {
                    propertyName: TemplateInputTypes.ExpirationTime,
                    value: time,
                },
                {
                    propertyName: TemplateInputTypes.Timezone,
                    value: timezone,
                },
            ]),
        );
        validator({
            [TemplateInputTypes.ExpirationType]: expirationDateTimeValue,
            [TemplateInputTypes.ExpirationPeriod]: expirationPeriodValue,
            [TemplateInputTypes.ExpirationDate]: expirationDateTimeValue,
            [TemplateInputTypes.ExpirationTime]: expirationDateTimeValue,
            [TemplateInputTypes.Timezone]: expirationDateTimeValue,
        });
    };

    const handleExpirationTypeChange = (option: Nullable<DropdownOption<string>>) => {
        const { value = null } = option ?? {};
        if (isNull(value)) {
            handleValidateAndChange(null, null, null, null, partnerTimeZone);
            return;
        }
        handleValidateAndChange(value, expirationPeriod, expirationDate, expirationTime, timezone);
    };

    const handleExpirationPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let period: Nullable<string | number> = e.target.value;
        if (period === '' || +period < 1) {
            period = null;
        } else {
            period = +period;
        }
        handleValidateAndChange(expirationType, period, expirationDate, expirationTime, timezone);
    };

    const handleExpirationDateChange = (date: Nullable<Date>) => {
        const tmpDate = normalizeDatePickerValue(date, partnerDateFormat);

        handleValidateAndChange(expirationType, expirationPeriod, tmpDate, expirationTime, timezone);
    };

    const handleExpirationTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const time: Nullable<string> = e.target.value ?? null;
        handleValidateAndChange(expirationType, expirationPeriod, expirationDate, time, timezone);
    };

    const handleTimezoneChange = (option: DropdownOption<number>) => {
        const timezone: Nullable<number> = option?.value ?? null;
        handleValidateAndChange(expirationType, expirationPeriod, expirationDate, expirationTime, timezone);
    };

    const deviceTypesChangeHandler = (e) => handleDeviceTypeChange(e.value, 0);

    const updateDynamicVariableBodyOptions = useCallback(
        (hintText) => {
            setInsertionOptions({
                channel: TemplateTypeEnum.DynamicPopUp,
                hintText,
                inputFilterKey: '',
                focusedInputKey: TemplateInputTypes.Body,
            });
        },
        [setInsertionOptions],
    );

    // The assistant works on a stringified JSON envelope instead of bare html for this channel.
    const requestBody = useMemo(() => buildDynamicPopUpRequestBody(bodyValue, deviceType), [bodyValue, deviceType]);

    // Dynamic pop-up has no subject, and only the html of the envelope is applied — the url,
    // platforms and expiration stay as the user configured them. An envelope the assistant could
    // not fill leaves the current template untouched instead of wiping it.
    const handleApplyAiTemplate = useCallback(
        ({ Body }: ProcessTemplateWithAiTemplate) => {
            const content = parseDynamicPopUpAiBody(Body);
            if (content) {
                handleChange(TemplateInputTypes.Body, content, 0);
            }
        },
        [handleChange],
    );

    const updateDynamicVariableUrlOptions = useCallback(() => {
        setInsertionOptions({
            channel: TemplateTypeEnum.DynamicPopUp,
            hintText: l.DynamicVariablesAreNotSupported,
            inputFilterKey: '',
            focusedInputKey: TemplateInputTypes.Url,
        });
    }, [setInsertionOptions]);

    useEffect(() => setUrl(urlTemplateData), [urlTemplateData]);

    const init = () => {
        doGetRequest(getPopUpComparisons.request, {
            successCallback: (Data) => {
                const popUpNotificationUrlComparisons = Data.map(({ Id, Name }) => ({ value: Id, label: Name }));
                setUrlTypesData(popUpNotificationUrlComparisons);
                if (isEmpty(templateData?.[TemplateInputTypes.UrlType])) {
                    handleUrlTypeChange(DeviceTypeOptions[0].value, 0);
                }
            },
        }).then(() => {
            setIsLoadingComparisons(false);
        });

        if (isEmpty(templateData?.[TemplateInputTypes.DeviceType])) {
            handleDeviceTypeChange(DeviceTypeOptions[0].value, 0);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div>
            <DynamicVariables
                dynamicVariables={dynamicVariables}
                clickHandler={insertionHandler}
                insertionOptions={insertionOptions}
            />
            <div className="dynamic-pop-up-url-section">
                {isDefaultLanguage && <TemplateCategoryInput value={categoryValue} onChange={handleCategoryChange} />}
                <div className="dynamic-pop-up-url-type">
                    {isNil(urlTypesData) || isEmpty(urlTypesData) || isLoadingComparisons ? (
                        <SkeletonLoader isBusy={true} />
                    ) : (
                        <Dropdown
                            value={templateData?.[TemplateInputTypes.UrlType]}
                            onChange={urlTypeChangeHandler}
                            data={urlTypesData}
                            labelAppearance="swap"
                            placeholder={t(l.UrlType)}
                        />
                    )}
                </div>
                <ExtendedInput
                    value={url}
                    onChange={urlChangeHandler}
                    labelAppearance="swap"
                    placeholder={t(l.Url)}
                    className="dynamic-pop-up-url-input"
                    onFocus={updateDynamicVariableUrlOptions}
                />
                <div className="dynamic-pop-up-device-type">
                    <Dropdown
                        value={templateData?.[TemplateInputTypes.DeviceType]}
                        onChange={deviceTypesChangeHandler}
                        data={translatedDeviceTypes}
                        labelAppearance="swap"
                        placeholder={t(l.DeviceType)}
                    />
                </div>
            </div>
            <div className="dynamic-pop-up-expiration-section">
                <Dropdown
                    className="dynamic-pop-up-expiration-input"
                    data={expirationTypeOptions}
                    value={expirationType}
                    label={t(l.ExpirationType)}
                    placeholder={t(l.ExpirationType)}
                    labelAppearance="swap"
                    onChange={handleExpirationTypeChange}
                    isValid={expirationTypeValidationState.isValid}
                    errorText={expirationTypeValidationState.errorText}
                    disabled={!isDefaultLanguage}
                    clearable={true}
                />
                <div>
                    {expirationType === ExpirationTypes.ExpirationPeriod && (
                        <ExtendedInput
                            className="dynamic-pop-up-expiration-input"
                            type="number"
                            value={expirationPeriod}
                            name={l.Days}
                            title={t(l.Days)}
                            label={t(l.Days)}
                            placeholder={t(l.Days)}
                            labelAppearance="swap"
                            min={1}
                            onChange={handleExpirationPeriodChange}
                            isValid={expirationPeriodValidationState.isValid}
                            errorText={expirationPeriodValidationState.errorText}
                            disabled={!isDefaultLanguage}
                        />
                    )}
                    {expirationType === ExpirationTypes.ExpirationDate && (
                        <div className="dynamic-pop-up-expiration-date-timezone">
                            <DatePickerInput
                                className="dynamic-pop-up-expiration-input"
                                value={expirationDate}
                                title={t(l.Date)}
                                placeholder={t(l.Date)}
                                labelAppearance="swap"
                                onChange={handleExpirationDateChange}
                                min={moment().format(DateToISOStringDateFormat)}
                                format={partnerDateFormat}
                                isTouched={true}
                                isValid={expirationDateValidationState.isValid}
                                errorText={expirationDateValidationState.errorText}
                                disabled={!isDefaultLanguage}
                            />
                            <TimePicker
                                name={l.Time}
                                className="dynamic-pop-up-time-picker"
                                labelAppearance="swap"
                                appearance="outline"
                                value={expirationTime}
                                onChange={handleExpirationTimeChange}
                                showSeconds={false}
                                isTouched={true}
                                isValid={expirationTimeValidationState.isValid}
                                errorText={expirationTimeValidationState.errorText}
                                disabled={!isDefaultLanguage}
                            />
                            <Dropdown
                                name={l.Timezone}
                                className="dynamic-pop-up-timezone"
                                flexibility="full-width"
                                hasSearch={true}
                                onChange={handleTimezoneChange}
                                value={timezone}
                                appearance="outline"
                                data={TIMEZONE_OPTIONS}
                                noDataText={t(l.NoDataFound)}
                                isValid={timezoneValidationState.isValid}
                                errorText={timezoneValidationState.errorText}
                                disabled={!isDefaultLanguage}
                            />
                        </div>
                    )}
                </div>
            </div>
            <HTMLEditors
                tabOptions={radioGroupOptions}
                htmlText={bodyValue}
                handleChange={handleBodyChange}
                config={uiConfig?.[templateFieldWithConfig]}
                handleConfigChange={handleBodyUIConfigChange}
                templateLangKey={templateLangKey}
                getAssets={getAssets}
                addAsset={addAsset}
                validationState={bodyValidationState}
                dynamicVariables={dynamicVariables}
                templateType={TemplateTypeEnum.DynamicPopUp}
                eventId={eventId}
                aiBody={requestBody}
                getPreviewHtml={parseDynamicPopUpAiBody}
                onApplyAiTemplate={handleApplyAiTemplate}
                bodyPropertyName={TemplateInputTypes.Body}
                updateInsertionHandler={updateBodyInsertionHandler}
                onEditorFocus={updateDynamicVariableBodyOptions}
                isInitialFocus={true}
            />
        </div>
    );
};

DynamicPopUpContent.propTypes = {
    templateData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleUIConfigChange: PropTypes.func.isRequired,
    templateLangKey: PropTypes.string.isRequired,
    getAssets: PropTypes.func,
    addAsset: PropTypes.func,
    dynamicVariables: PropTypes.arrayOf(PropTypes.object),
    eventId: PropTypes.number,
};

export default DynamicPopUpContent;
